import { Component, OnInit } from '@angular/core';
import { COMPANIES_ID } from 'projects/common/src/app/enumerables/companies-id';
import { IUser } from 'projects/common/src/app/interfaces';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { CacheService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { ReportDataService } from '../../services/report-data/report-data.service';

@Component({
  selector: 'app-report-messenger',
  templateUrl: './report-messenger.component.html',
  styleUrls: ['./report-messenger.component.scss'],
})
export class ReportMessengerComponent implements OnInit {
  public filterConfig: IFilterConfig;
  public tableConfig: ITableConfig;
  public canDownload: boolean;
  public params: any;
  public message: string;
  public isDisabledFilterBtn: boolean;
  public systemError: string;
  public isMtv = false;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  constructor(
    private tableConfigService: TableConfigService,
    private cacheService: CacheService,
    private reportDataService: ReportDataService,
    private languageService: LanguageService,
    private layoutConfigService: LayoutConfigService,
    private datePipe: DatePresentationPipe,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
  ) {
    this.isDisabledFilterBtn = false;
    this.disabledExcelBtn = false;

    const companyId = this.cacheService.local('session').user.company.id;
    this.isMtv = companyId === COMPANIES_ID.MTV;

    if (this.isMtv) {
      this.filterConfig = {
        dates: { required: true, options: { daysRange: 6, minDayRange: 6 } },
        shift: { required: true },
        machineType: { required: true, isForMtv: true },
        machineList: { required: true, multiselect: true },
        cleanButton: true,
      };
    } else {
      this.filterConfig = {
        dates: { required: true, options: { daysRange: 6, minDayRange: 6 } },
        shift: { required: true },
        machineType: { required: true, multiselect: false },
        machineList: { required: true, multiselect: true },
        cleanButton: true,
      };
    }

    this.canDownload = false;
  }

  /**
   * Init component
   */
  public ngOnInit(): void {
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle: 'messages',
      imageKey: 'report',
      imageType: 'section',
    };

    this.layoutConfigService.setConfig(config);
  }

  /**
   * Function to create or modify table data
   *
   * @param $event Event on change filters
   */
  public async createTableData($event: IOutputFilter): Promise<void> {
    this.filterConfig.isLoading = true;
    this.canDownload = false;
    const user = this.cacheService.local('session').user as IUser;
    this.params = {
      startDate: $event.startDate,
      endDate: $event.endDate,
      shiftId: $event.shiftId,
      machineList: $event.machineId,
      machineTypeList: $event.machineTypeId,
      companyId: user.company.id,
    };
    try {
      const response = await this.reportDataService.getMessageReportData(this.params);
      if (response && response.length > 0) {
        this.canDownload = true;
        this.setTableConfig(response);
        this.message = '';
      } else {
        this.tableConfig = null;
        this.canDownload = false;
        this.message = this.languageService.message('m034');
      }
      this.isDisabledFilterBtn = false;
      this.systemError = '';
    } catch ({ name }) {
      this.canDownload = false;
      this.systemError = name === 'TimeoutError' ? 'm014' : 'm017';
    } finally {
      this.filterConfig.isLoading = false;
    }
    const filters = {
      reports: 'report-messenger',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * executes excel donwload from endpoint recieved file
   */
  public downloadExcel(): void {
    const selectRangeDate = {
      startDate: this.datePipe.transform(this.params.startDate),
      endDate: this.datePipe.transform(this.params.endDate),
    };
    this.btnSpinner = true;
    this.disabledExcelBtn = true;
    this.reportDataService.getMessageReportExcel(this.params).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, 'messenger-status', selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }

  /**
   * Function to set a table by config
   */
  private setTableConfig(tableData: any[]): void {
    const excludeColumns = ['messageSyncId', 'shiftId'];
    const largeColumns = ['body'];

    // Create config of headers
    const headerParams: ITableHeaderParams = {
      data: tableData,
      excludeColumns,
      largeColumns,
      sortables: ['machineName', 'createdAt'],
      replaceColumnName: [{ prevName: 'machineTypeName', newName: 'machine_type' }],
    };

    // Create config of dataset
    const datasetParams: ITableDataParams = {
      tableData,
      colorConfig: [{ field: 'machineName', isBold: true }],
      dateConfig: [{ field: 'createdAt', format: 'date-time' }],
      excludeColumns,
      largeColumns,
    };

    const header = this.tableConfigService.setHeaderConfig(headerParams);
    const dataset = this.tableConfigService.setDatasetConfig(datasetParams);

    this.tableConfig = { header, dataset };
  }
}
